// The quote flow's account station: pure rules for who is offered "Save
// your quote", and how the account-grant answer is read. The grant is the
// server-held authorization that lets the sign-in screen confirm the
// channels typed at submission; it never carries a destination the
// visitor chose.

import type { LoginGrantState } from "helpers/login-grant";
import { passwordSignInPath } from "pages/StartLead/signIn";

import type { QuoteFlowStepName } from "./analytics";

/** The only quotes a lead account can be created from: the ones the
 * unified lead form made. Any other quote's grant request is refused. */
export const LEAD_QUOTE_ORIGIN = "lead_submission";

export function canCreateAccountFrom(originKind: string | null): boolean {
  return originKind === LEAD_QUOTE_ORIGIN;
}

/** The stations from the quote screen onward where the bar stands. The
 * account station itself and the finished flow (which carries its own
 * "Create your account") are left out. */
const SAVE_BAR_STATIONS: readonly QuoteFlowStepName[] = [
  "quote",
  "application",
  "review",
  "payment",
  "sign",
  "finalize",
];

export function saveQuoteBarVisible(args: {
  station: QuoteFlowStepName | null;
  originKind: string | null;
  signedIn: boolean;
  /** The user context is still resolving: showing the bar to a signed-in
   * visitor for a moment would be wrong, so wait. */
  userLoading: boolean;
  dismissed: boolean;
}): boolean {
  const { station, originKind, signedIn, userLoading, dismissed } = args;
  if (signedIn || userLoading || dismissed) return false;
  if (!canCreateAccountFrom(originKind)) return false;
  return station !== null && SAVE_BAR_STATIONS.includes(station);
}

export type AccountGrantAnswer =
  { kind: "existing_account" } | { kind: "grant"; state: LoginGrantState };

const GRANT_TOKEN = /^[0-9a-f]{64}$/;

/** Reads the account-grant answer defensively. An answer that names
 * neither an existing account nor a well-formed grant with a usable
 * method is treated as no answer. */
export function parseAccountGrant(
  raw: unknown,
  quoteId: string,
): AccountGrantAnswer | null {
  if (typeof raw !== "object" || raw === null) return null;
  const body = raw as Record<string, unknown>;
  if (body.existing_account === true) return { kind: "existing_account" };
  if (typeof body.grant !== "string" || !GRANT_TOKEN.test(body.grant)) {
    return null;
  }
  if (!Array.isArray(body.methods) || body.methods.length === 0) return null;
  return {
    kind: "grant",
    state: { loginGrant: body.grant, leadKind: "planning", quoteId },
  };
}

/** What asking for an account grant came to. `not_available` is the one
 * answer for every refusal, so a forwarded link and a stale quote read the
 * same. */
export type AccountGrantResult =
  | { kind: "granted"; state: LoginGrantState }
  | { kind: "existing_account" }
  | { kind: "not_available" }
  | { kind: "rate_limited" }
  | { kind: "failed" };

/** The result for a request the server refused or that never got an
 * answer. */
export function accountGrantFailureOf(
  statusCode: number | undefined,
): Exclude<AccountGrantResult, { kind: "granted" | "existing_account" }> {
  if (statusCode === 404) return { kind: "not_available" };
  if (statusCode === 429) return { kind: "rate_limited" };
  return { kind: "failed" };
}

/** The in-flow path the grant's sign-in lands on; the server fixes the
 * same value on the grant, this is only the link back to it. */
export function accountReturnPath(quoteId: string): string {
  return `/quotes/${encodeURIComponent(quoteId)}/account`;
}

/** Where a visitor who cannot create a lead account goes: the normal
 * sign-in, coming back to the quote afterwards. */
export function signInPathForQuote(quoteId: string): string {
  return passwordSignInPath(`/quotes/${encodeURIComponent(quoteId)}`);
}

const DISMISS_KEY_PREFIX = "after.save_quote_bar.dismissed.";

export function readBarDismissed(quoteId: string): boolean {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY_PREFIX + quoteId) === "1";
  } catch {
    return false;
  }
}

export function writeBarDismissed(quoteId: string): void {
  try {
    window.sessionStorage.setItem(DISMISS_KEY_PREFIX + quoteId, "1");
  } catch {
    // Storage can be unavailable; the bar then reappears on the next load.
  }
}

export interface PolicyDestination {
  to: string;
  label: string;
}

/** Where a signed-in person who finished a policy quote goes next: the
 * policy itself when the finalize view names it, otherwise their home. */
export function finishedPolicyDestination(
  policyId: string | null | undefined,
): PolicyDestination {
  if (policyId) {
    return {
      to: `/policies/${encodeURIComponent(policyId)}`,
      label: "View your policy",
    };
  }
  return { to: "/", label: "Go to your account" };
}
